import React from 'react';
import {
  Paper,
  Box,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Divider,
  Chip,
  Button,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Person as PersonIcon,
  Webhook as WebhookIcon,
  Schedule as ScheduleIcon,
  NotificationsActive as NotifyIcon,
} from '@mui/icons-material';
import type { WorkflowStepEditorProps } from './types';
import { PRIORITAET_OPTIONEN } from './utils';

const WorkflowStepEditor: React.FC<WorkflowStepEditorProps> = ({
  schritt,
  index,
  canRemove,
  onRemove,
  onChange,
  showAutomation = false,
  availableUsers = [],
  availableWebhooks = [],
}) => {
  const hasAutoNotify = 'autoNotify' in schritt;
  const hasAutoZuweisung = 'autoZuweisung' in schritt;

  return (
    <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1">Schritt {schritt.schrittNummer}</Typography>
        {canRemove && (
          <IconButton size="small" onClick={() => onRemove(index)} color="error">
            <DeleteIcon />
          </IconButton>
        )}
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Titel"
            value={schritt.titel}
            onChange={(e) => onChange(index, 'titel', e.target.value)}
            required
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Priorität</InputLabel>
            <Select
              value={schritt.prioritaet}
              onChange={(e) => onChange(index, 'prioritaet', e.target.value)}
              label="Priorität"
            >
              {PRIORITAET_OPTIONEN.map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Was (Aufgabenbeschreibung)"
            value={schritt.was}
            onChange={(e) => onChange(index, 'was', e.target.value)}
            required
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Wie (optional)"
            value={schritt.wie || ''}
            onChange={(e) => onChange(index, 'wie', e.target.value)}
            placeholder="Hinweise zur Durchführung..."
          />
        </Grid>

        {/* Automatisierung - nur bei Vorlagen */}
        {showAutomation && hasAutoZuweisung && (
          <>
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }}>
                <Chip icon={<ScheduleIcon />} label="Automatisierung" size="small" />
              </Divider>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Automatische Zuweisung</InputLabel>
                <Select
                  value={(schritt as { autoZuweisung?: number }).autoZuweisung || ''}
                  onChange={(e) => onChange(index, 'autoZuweisung', e.target.value || undefined)}
                  label="Automatische Zuweisung"
                >
                  <MenuItem value="">
                    <em>Keine automatische Zuweisung</em>
                  </MenuItem>
                  {availableUsers.map((user) => (
                    <MenuItem key={user.benutzerID} value={user.benutzerID}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon fontSize="small" />
                        {user.vorname} {user.nachname}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Frist (Tage nach Erstellung)"
                value={(schritt as { autoFrist?: number }).autoFrist || ''}
                onChange={(e) => onChange(index, 'autoFrist', e.target.value ? parseInt(e.target.value) : undefined)}
                InputProps={{ inputProps: { min: 1, max: 365 } }}
                helperText="Automatische Frist in Tagen"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Webhook auslösen</InputLabel>
                <Select
                  value={(schritt as { autoWebhookID?: number }).autoWebhookID || ''}
                  onChange={(e) => onChange(index, 'autoWebhookID', e.target.value || undefined)}
                  label="Webhook auslösen"
                >
                  <MenuItem value="">
                    <em>Kein Webhook</em>
                  </MenuItem>
                  {availableWebhooks.map((webhook) => (
                    <MenuItem key={webhook.webhookID} value={webhook.webhookID}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <WebhookIcon fontSize="small" />
                        {webhook.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {hasAutoNotify && (
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, height: '100%' }}>
                  <Button
                    variant={(schritt as { autoNotify?: boolean }).autoNotify ? 'contained' : 'outlined'}
                    size="small"
                    startIcon={<NotifyIcon />}
                    onClick={() => onChange(index, 'autoNotify', !(schritt as { autoNotify?: boolean }).autoNotify)}
                    color={(schritt as { autoNotify?: boolean }).autoNotify ? 'success' : 'inherit'}
                  >
                    {(schritt as { autoNotify?: boolean }).autoNotify ? 'Benachrichtigung aktiv' : 'Benachrichtigung deaktiviert'}
                  </Button>
                </Box>
              </Grid>
            )}
          </>
        )}
      </Grid>
    </Paper>
  );
};

export default WorkflowStepEditor;
